import { IconButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { Conversation } from "common/types";
import React from "react";
import { defaultColorPallete } from "utils";
import SendSharpIcon from "@mui/icons-material/SendSharp";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { LiveAudioVisualizer } from "react-audio-visualize";
import useAudioRecorderLogic from "./useAudioRecorderLogic";
import Loading from "components/Loading";

type Props = {
  onCloseAudioRecorder: (show: boolean) => void;
  requestSendAudioMessage: (
    audioBlob: Blob,
    fileName?: string,
    onSuccess?: VoidFunction,
    onFailed?: VoidFunction,
    onFinally?: VoidFunction
  ) => Promise<void>;
  selectedConversation: Conversation;
  selectedDevice: string;
};

export default function AudioRecorder(props: Props) {
  const {
    deleteAudio,
    recordingTime,
    isPaused,
    isRecording,
    mediaRecorder,
    togglePauseResumeRecording,
    previewBlob,
    audioDuration,
    requestSendAudio,
    shouldSendAudio,
  } = useAudioRecorderLogic(props);

  return (
    <Box
      display="flex"
      justifyContent="center"
      gap={2}
      width={{ xs: "100%", lg: "60%", xl: "40%" }}
    >
      {shouldSendAudio && <Loading />}
      {!shouldSendAudio && (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width={{ xs: "70%", md: "60%", lg: "90%" }}
          >
            <IconButton
              onClick={deleteAudio}
              sx={{
                bgcolor: defaultColorPallete.neutral.main,
                width: 55,
                height: 55,
                "&:hover": {
                  bgcolor: defaultColorPallete.error.main,
                },
              }}
            >
              <DeleteIcon htmlColor="white" />
            </IconButton>
            {!isPaused && isRecording && mediaRecorder && !previewBlob ? (
              <>
                <Typography>{formatDuration(audioDuration)}</Typography>
                <LiveAudioVisualizer
                  mediaRecorder={mediaRecorder}
                  height="20"
                  barColor={`${defaultColorPallete.primary.main}`}
                />
              </>
            ) : (
              previewBlob && (
                <audio
                  style={{ width: "100%" }}
                  controls
                  src={URL.createObjectURL(previewBlob)}
                />
              )
            )}
            <IconButton
              onClick={togglePauseResumeRecording}
              sx={{
                color: "white",
                bgcolor: defaultColorPallete.error.main,
                width: 55,
                height: 55,
                "&:hover": {
                  color: defaultColorPallete.error.main,
                  border: `1px solid ${defaultColorPallete.error.main}`,
                  bgcolor: "inherit",
                },
              }}
            >
              {isPaused ? <MicIcon /> : <PauseCircleOutlineIcon />}
            </IconButton>
          </Box>
          <IconButton
            onClick={requestSendAudio}
            sx={{
              bgcolor: defaultColorPallete.primary.main,
              width: 55,
              height: 55,
            }}
          >
            <SendSharpIcon htmlColor="white" />
          </IconButton>
        </>
      )}
    </Box>
  );
}

function formatDuration(sec: number): string {
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
}
